"use client";

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { playgroundQueryKeys } from "./query-keys";
import {
  deleteSnippet,
  deleteSnippetVersion,
  explainCode,
  fetchIdePlan,
  fetchLanguages,
  fetchMySnippets,
  fetchPlaygroundHealth,
  fetchRuntimeCatalogue,
  fetchSharedSnippet,
  fetchSnippetById,
  fetchSnippetVersion,
  fetchSnippetVersions,
  fixError,
  forkSnippet,
  runCode,
  saveSnippet,
  shareSnippet,
  unshareSnippet,
  updateSnippet,
  type ListMySnippetsParams,
} from "./api";
import type {
  CodeFixResult,
  ExplainCodeInput,
  FixErrorInput,
  RunCodeInput,
  SaveSnippetInput,
  UpdateSnippetInput,
} from "./types";
import { SNIPPET_OBJECT_ID_REGEX } from "./types";
import { shouldRetryExplainMutation } from "./lib/explain-operation";
import { shouldRetryFixMutation } from "./lib/fix-operation";

export function useRunCode() {
  return useMutation({
    mutationFn: (input: RunCodeInput) => runCode(input),
  });
}

/**
 * `ask` is another route with Fix My Error's answer (`EX-HNT01`: the practice
 * page's, which reads the program itself); absent is the Playground's.
 */
export function useFixError(ask?: (input: FixErrorInput) => Promise<CodeFixResult>) {
  return useMutation({
    mutationFn: (input: FixErrorInput) => (ask ? ask(input) : fixError(input)),
    retry: shouldRetryFixMutation,
  });
}

export function useExplainCode() {
  return useMutation({
    mutationFn: (input: ExplainCodeInput) => explainCode(input),
    retry: shouldRetryExplainMutation,
  });
}

export function useLanguages() {
  return useQuery({
    queryKey: playgroundQueryKeys.languages,
    queryFn: () => fetchLanguages(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The runtime catalogue and its discovery state (`PG-IDE01`).
 *
 * The selector renders from the bundled contract and never waits on this — it
 * only adds "and here is what confirmed it", so a failure is a missing badge
 * rather than a missing picker. `retry: false` for the same reason.
 */
export function useRuntimeCatalogue() {
  return useQuery({
    queryKey: playgroundQueryKeys.runtimes,
    queryFn: () => fetchRuntimeCatalogue(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function usePlaygroundHealth(enabled = true) {
  return useQuery({
    queryKey: playgroundQueryKeys.health,
    queryFn: () => fetchPlaygroundHealth(),
    staleTime: 60 * 1000,
    enabled,
  });
}

export function useIdePlan(enabled = true) {
  return useQuery({
    queryKey: playgroundQueryKeys.idePlan,
    queryFn: () => fetchIdePlan(),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * The owner's snippet list. The params are the key, so a response for one
 * search or page can never be shown under another (`PG-UX06`); with
 * `keepPreviousData` the last page stays on screen, flagged `isPlaceholderData`,
 * while the next one loads.
 */
export function useMySnippets(
  params?: ListMySnippetsParams,
  enabled = true,
  options: { keepPreviousData?: boolean } = {},
) {
  return useQuery({
    queryKey: playgroundQueryKeys.mySnippets(params ?? {}),
    queryFn: () => fetchMySnippets(params),
    enabled,
    ...(options.keepPreviousData ? { placeholderData: keepPreviousData } : {}),
  });
}

export function useSnippetById(id: string, enabled = true) {
  const isObjectId = SNIPPET_OBJECT_ID_REGEX.test(id);
  return useQuery({
    queryKey: playgroundQueryKeys.snippetById(id),
    queryFn: () => fetchSnippetById(id),
    enabled: enabled && isObjectId,
  });
}

export function useSharedSnippet(shareSlug: string, enabled = true) {
  const isSlug = shareSlug.length > 0 && !SNIPPET_OBJECT_ID_REGEX.test(shareSlug);
  return useQuery({
    queryKey: playgroundQueryKeys.sharedSnippet(shareSlug),
    queryFn: () => fetchSharedSnippet(shareSlug),
    enabled: enabled && isSlug,
  });
}

export function useSaveSnippet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveSnippetInput) => saveSnippet(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["playground", "mySnippets"] });
    },
  });
}

export function useUpdateSnippet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSnippetInput }) =>
      updateSnippet(id, input),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ["playground", "mySnippets"] });
      void queryClient.invalidateQueries({ queryKey: playgroundQueryKeys.snippetById(id) });
      // A save that changed the content wrote a version, so the history the
      // panel is showing is one row out of date (`PG-IDE05`).
      void queryClient.invalidateQueries({ queryKey: playgroundQueryKeys.snippetVersions(id) });
    },
  });
}

/** Owner version history for one snippet (`PG-IDE05`). */
export function useSnippetVersions(id: string, enabled = true) {
  return useQuery({
    queryKey: playgroundQueryKeys.snippetVersions(id),
    queryFn: () => fetchSnippetVersions(id),
    enabled: enabled && SNIPPET_OBJECT_ID_REGEX.test(id),
  });
}

/** One version with its source; fetched only when a row is selected. */
export function useSnippetVersion(id: string, versionId: string | null) {
  return useQuery({
    queryKey: playgroundQueryKeys.snippetVersion(id, versionId ?? ""),
    queryFn: () => fetchSnippetVersion(id, versionId!),
    enabled: Boolean(versionId) && SNIPPET_OBJECT_ID_REGEX.test(id),
  });
}

export function useDeleteSnippetVersion(snippetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) => deleteSnippetVersion(snippetId, versionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: playgroundQueryKeys.snippetVersions(snippetId),
      });
    },
  });
}

export function useDeleteSnippet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSnippet(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ["playground", "mySnippets"] });
      void queryClient.removeQueries({ queryKey: playgroundQueryKeys.snippetById(id) });
    },
  });
}

export function useForkSnippet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => forkSnippet(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["playground", "mySnippets"] });
    },
  });
}

export function useShareSnippet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password?: string | null }) =>
      shareSnippet(id, { password }),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ["playground", "mySnippets"] });
      void queryClient.invalidateQueries({ queryKey: playgroundQueryKeys.snippetById(id) });
    },
  });
}

export function useUnshareSnippet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unshareSnippet(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ["playground", "mySnippets"] });
      void queryClient.invalidateQueries({ queryKey: playgroundQueryKeys.snippetById(id) });
    },
  });
}
